/**
 * Recetas (Hablando con AROC): reportes que gustaron, repetidos sin el LLM.
 * Sólo administradores; el backend responde 503 si están apagadas.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";

export interface PasoReceta {
  id: string;
  tool: string;
  params: Record<string, unknown>;
}

export interface SpecReceta {
  formato: "docx" | "xlsx";
  titulo: string;
  resumen: string;
  pasos: PasoReceta[];
  secciones: {
    titulo: string;
    parrafos: string[];
    paso: string | null;
    ruta: string;
    columnas: { titulo: string; campo: string }[];
  }[];
}

export interface BorradorReceta {
  spec: SpecReceta;
  muestra: { titulo: string; columnas: string[]; filas: string[][] }[];
  costo_borrador_usd: number | null;
  costo_platica_usd: number | null;
}

export type Periodicidad =
  | { tipo: "una_vez"; fecha: string; hora: string }
  | { tipo: "diario"; hora: string }
  | { tipo: "semanal"; dia_semana: number; hora: string }
  | { tipo: "mensual"; dia_mes: number; hora: string };

export interface CorridaReceta {
  id: string;
  inicio: string;
  fin: string | null;
  estado: "corriendo" | "ok" | "error";
  manual: boolean;
  costo_usd: number | null;
  error: string | null;
  descarga: string | null;
}

export interface Receta {
  id: string;
  nombre: string;
  formato: string;
  titulo: string;
  pasos: PasoReceta[];
  periodicidad: Periodicidad;
  periodicidad_texto: string;
  destino: "telegram" | "ninguno";
  resumen_llm: boolean;
  max_corridas_dia: number;
  pausada: boolean;
  next_run_at: string | null;
  ultima_corrida: CorridaReceta | null;
  costo_platica_usd: number | null;
  costo_borrador_usd: number | null;
}

const KEY = ["recetas"] as const;

export function useRecetasEstado() {
  return useQuery({
    queryKey: ["recetas", "estado"],
    queryFn: () => api<{ enabled: boolean }>("/api/v1/recetas/estado"),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRecetas(enabled = true) {
  return useQuery({
    queryKey: KEY,
    queryFn: () => api<Receta[]>("/api/v1/recetas"),
    enabled,
  });
}

export function useBorradorReceta() {
  return useMutation({
    mutationFn: (body: { session_id: string; documento_id: string }) =>
      api<BorradorReceta>("/api/v1/recetas/borrador", { method: "POST", body }),
  });
}

export function useCrearReceta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      nombre: string;
      spec: SpecReceta;
      periodicidad: Periodicidad;
      destino: "telegram" | "ninguno";
      resumen_llm: boolean;
      max_corridas_dia: number;
      origen_session_id: string;
      costo_platica_usd: number | null;
      costo_borrador_usd: number | null;
    }) => api<Receta>("/api/v1/recetas", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useAccionReceta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, accion }: { id: string; accion: "pausar" | "reanudar" | "ejecutar" | "borrar" }) =>
      accion === "borrar"
        ? api<void>(`/api/v1/recetas/${id}`, { method: "DELETE" })
        : api<unknown>(`/api/v1/recetas/${id}/${accion}`, { method: "POST" }),
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
